// Personnages low-poly : le squelette Mixamo (et ses animations) est gardé, sa peau réaliste est cachée, et chaque os porte
// un bloc à facettes (tête, buste, bras, jambes…). Les blocs suivent l'animation au millimètre, sans pondération de peau.
// Le casque, le gilet et le sac sont fixés aux mêmes os : ils tombent toujours juste.
import * as THREE from 'three';
import { flat } from './lowpoly';
import { buildHelmet, buildVest } from './gear';

export interface Palette { skin: number; shirt: number; pants: number; boots: number; gloves: number; hair: number }
export const PALETTES = {
  soldier: { skin: 0xc49a7c, shirt: 0x5b6340, pants: 0x4c5238, boots: 0x2b241e, gloves: 0x2a2a28, hair: 0x3a2c22 },
  bandits: [
    { skin: 0xb98d6e, shirt: 0x5a4a3a, pants: 0x3d3a35, boots: 0x231f1b, gloves: 0x2a2826, hair: 0x231c17 },
    { skin: 0xd0a888, shirt: 0x3f4753, pants: 0x4a4f55, boots: 0x262422, gloves: 0x232323, hair: 0x5a4430 },
    { skin: 0x9c7458, shirt: 0x6b5b3a, pants: 0x3e3b33, boots: 0x2a221b, gloves: 0x2b2b29, hair: 0x1d1814 },
    { skin: 0xc49a7c, shirt: 0x33372f, pants: 0x55503f, boots: 0x231f1b, gloves: 0x222, hair: 0x6b4d33 },
  ],
  infected: [
    { skin: 0x8e9879, shirt: 0x6e6455, pants: 0x3f4650, boots: 0x2c2a26, gloves: 0x8e9879, hair: 0x2c2a26 },
    { skin: 0x9aa08a, shirt: 0x4f5b66, pants: 0x5a5040, boots: 0x2a2724, gloves: 0x9aa08a, hair: 0x3b3027 },
    { skin: 0x858f72, shirt: 0x7a3b30, pants: 0x3a3f46, boots: 0x262321, gloves: 0x858f72, hair: 0x1f1b18 },
  ],
  // Costaud (chair grise, tenue de chantier), hurleur (blafard), rampant (terreux)
  brute: { skin: 0x7c826c, shirt: 0x8a6a2c, pants: 0x34383d, boots: 0x22201d, gloves: 0x7c826c, hair: 0x1c1a17 },
  screamer: { skin: 0xb9bca8, shirt: 0x5f6870, pants: 0x4a4c50, boots: 0x2a2826, gloves: 0xb9bca8, hair: 0x9a9a8a },
  crawler: { skin: 0x7a7a5c, shirt: 0x4a4436, pants: 0x3a3830, boots: 0x262321, gloves: 0x7a7a5c, hair: 0x2a2620 },
} satisfies Record<string, Palette | Palette[]>;

const GEAR = { helmet: 0x55603a, helmetDark: 0x434c2d, vest: 0x4a5236, pouch: 0x3e4530, pack: 0x5a6339, packDark: 0x464d2c };

// Os → os suivant, largeur et profondeur en mètres, couleur
type Part = keyof Palette;
const SEGMENTS: [string, string | number, number, number, Part][] = [
  ['Spine', 'Spine1', 0.32, 0.2, 'shirt'],
  ['Spine1', 'Spine2', 0.34, 0.21, 'shirt'],
  ['Spine2', 'Neck', 0.38, 0.23, 'shirt'],
  ['Neck', 'Head', 0.11, 0.11, 'skin'],
  ['Head', 'HeadTop_End', 0.2, 0.23, 'skin'],
  ['LeftShoulder', 'LeftArm', 0.11, 0.11, 'shirt'], ['RightShoulder', 'RightArm', 0.11, 0.11, 'shirt'],
  ['LeftArm', 'LeftForeArm', 0.1, 0.1, 'shirt'], ['RightArm', 'RightForeArm', 0.1, 0.1, 'shirt'],
  ['LeftForeArm', 'LeftHand', 0.085, 0.085, 'shirt'], ['RightForeArm', 'RightHand', 0.085, 0.085, 'shirt'],
  ['LeftHand', 0.09, 0.08, 0.04, 'gloves'], ['RightHand', 0.09, 0.08, 0.04, 'gloves'],
  ['LeftUpLeg', 'LeftLeg', 0.15, 0.15, 'pants'], ['RightUpLeg', 'RightLeg', 0.15, 0.15, 'pants'],
  ['LeftLeg', 'LeftFoot', 0.12, 0.12, 'pants'], ['RightLeg', 'RightFoot', 0.12, 0.12, 'pants'],
  ['LeftFoot', 'LeftToeBase', 0.11, 0.13, 'boots'], ['RightFoot', 'RightToeBase', 0.11, 0.13, 'boots'],
  ['LeftToeBase', 'LeftToe_End', 0.1, 0.08, 'boots'], ['RightToeBase', 'RightToe_End', 0.1, 0.08, 'boots'],
];

// Hauteur du bord du casque sur la tête (fraction de la longueur Head → HeadTop_End), juste au-dessus des yeux
const HELMET_AT = 0.6;

const bone = (root: THREE.Object3D, n: string) => root.getObjectByName(`mixamorig${n}`) ?? null;

// Repère d'un os : Y vers l'os suivant, Z vers l'avant, X sur le côté. Dans un squelette Mixamo, l'axe Z local
// des os du tronc et de la tête pointe vers l'avant du personnage : on s'en sert pour orienter les blocs.
function basis(_b: THREE.Object3D, axisLocal: THREE.Vector3) {
  const y = axisLocal.clone().normalize();
  const hint = Math.abs(y.z) > 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(0, 0, 1);
  const z = hint.sub(y.clone().multiplyScalar(hint.dot(y))).normalize();
  const x = new THREE.Vector3().crossVectors(y, z).normalize();
  return new THREE.Matrix4().makeBasis(x, y, z);
}

// Bloc le long de l'os : de 0 à len sur Y, largeur w (X), profondeur d (Z) ; tailles dans le repère local de l'os
function block(len: number, w: number, d: number, from = 0) {
  const g = new THREE.BoxGeometry(w, len, d);
  g.translate(0, from + len / 2, 0);
  return g;
}

// Équipement sculpté pour Silex (casques, gilets, sacs) : modélisé dans le repère de Silex en pose en T
// (mètres, pieds en Y = 0, face à +Z), comme le fichier d'origine dont voici la boîte. Chargé par loadActors.
export const GEAR_IDS = ['h_hard', 'h_mil', 'v_soft', 'v_police', 'v_mil', 'b_small', 'b_patrol', 'b_assault', 'b_raid'];
export const GEAR_MODELS = new Map<string, THREE.Object3D>();
const SILEX_MIN = new THREE.Vector3(-0.9098, 0, -0.13725), SILEX_H = 1.8;
const GEAR_BONE: Record<string, string> = { h: 'Head', v: 'Spine2', b: 'Spine2' };

export class LowBody {
  private helmets = new Map<string, THREE.Object3D | null>(); // un modèle par type de casque, créé à la demande
  private vests = new Map<string, THREE.Object3D | null>();
  private hair: THREE.Mesh | null = null;
  private head: THREE.Mesh | null = null; // tête entière, et tête coupée sous le casque
  private headCut: THREE.Mesh | null = null;
  private pack: THREE.Object3D | null = null;
  private scaleOf = new Map<THREE.Object3D, number>();

  // sculpted : le modèle a son propre corps (silex) ; on ne fait que l'habiller et le recolorer
  private sculpted = false;
  private fit: THREE.Matrix4 | null = null; // repère d'origine de Silex → monde, en pose en T
  constructor(private root: THREE.Object3D, pal: Palette, private infected = false, sculpted = false) {
    root.updateMatrixWorld(true);
    if (sculpted) {
      this.sculpted = true;
      // Le fichier riggé n'a ni la même échelle ni la même origine que l'original : on cale par la boîte en pose en T
      const box = new THREE.Box3().setFromObject(root), k = (box.max.y - box.min.y) / SILEX_H;
      this.fit = new THREE.Matrix4().makeTranslation(box.min.x + (box.max.x - box.min.x) / 2, box.min.y, box.min.z)
        .multiply(new THREE.Matrix4().makeScale(k, k, k))
        .multiply(new THREE.Matrix4().makeTranslation(0, -SILEX_MIN.y, -SILEX_MIN.z));
      this.recolor(pal);
      return;
    }
    // La peau Mixamo reste pour l'animation mais n'est plus dessinée
    root.traverse(o => { if ((o as THREE.SkinnedMesh).isSkinnedMesh) o.visible = false; });
    for (const [from, to, w, d, part] of SEGMENTS) {
      const b = bone(root, from);
      if (!b) continue;
      const k = this.k(b);
      const child = typeof to === 'string' ? bone(root, to) : null;
      const axis = child ? child.position.clone() : new THREE.Vector3(0, (to as number) / k, 0);
      if (axis.lengthSq() < 1e-8) continue;
      const len = axis.length();
      const geo = block(len * (part === 'skin' && from === 'Head' ? 1 : 1.02), w / k, d / k);
      geo.applyMatrix4(basis(b, axis));
      const m = this.add(b, geo, pal[part]);
      if (from === 'Head') {
        // Sous un casque, la tête s'arrête au-dessus des yeux : les coins du bloc ne traversent plus la coque
        this.head = m;
        const cut = block(len * HELMET_AT, w / k, d / k);
        cut.applyMatrix4(basis(b, axis));
        this.headCut = this.add(b, cut, pal[part]);
        this.headCut.visible = false;
        this.face(b, axis, k, pal);
      }
    }
    // Bassin : bloc centré sur les hanches
    const hips = bone(root, 'Hips');
    if (hips) {
      const k = this.k(hips), sp = bone(root, 'Spine');
      const geo = block(0.24 / k, 0.34 / k, 0.22 / k, -0.14 / k);
      geo.applyMatrix4(basis(hips, sp ? sp.position.clone() : new THREE.Vector3(0, 1, 0)));
      this.add(hips, geo, pal.pants);
    }
  }

  // Modèle sculpté : chaque famille de matériaux (treillis, pantalon, peau, cheveux, gants, bottes) prend la couleur de la tenue,
  // en gardant ses nuances (plus clair, plus foncé). La tenue de base du soldat garde les couleurs d'origine.
  private recolor(pal: Palette) {
    if (pal === PALETTES.soldier) return;
    const FAMILY: [RegExp, keyof Palette, string][] = [
      [/^olive/, 'shirt', 'olivemat'], [/^field/, 'pants', 'fieldmat'], [/^skin/, 'skin', 'skinmat'],
      [/^hair/, 'hair', 'hairmat'], [/^graphite|^rubber/, 'gloves', 'graphitemat'], [/^leather/, 'boots', 'leathermat'],
    ];
    const base = new Map<string, THREE.Color>();
    this.root.traverse(o => { if ((o as THREE.Mesh).isMesh) for (const m of [(o as THREE.Mesh).material].flat() as THREE.MeshStandardMaterial[]) base.set(m.name, m.color.clone()); });
    this.root.traverse(o => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      const mats = ([mesh.material].flat() as THREE.MeshStandardMaterial[]).map(m => {
        const f = FAMILY.find(([re]) => re.test(m.name));
        if (!f) return m;
        const ref = base.get(f[2]) ?? m.color, c = m.clone();
        // Nuance d'origine (rapport à la couleur principale de la famille) appliquée à la couleur de la tenue
        const shade = (m.color.r + m.color.g + m.color.b) / Math.max(1e-3, ref.r + ref.g + ref.b);
        c.color.setHex(pal[f[1]]).multiplyScalar(Math.min(1.6, shade));
        return c;
      });
      mesh.material = Array.isArray(mesh.material) ? mats : mats[0]!;
    });
  }

  // Mètres → unités locales de l'os (le squelette Mixamo est en centimètres, mis à l'échelle)
  private k(b: THREE.Object3D) {
    if (!this.scaleOf.has(b)) this.scaleOf.set(b, b.getWorldScale(new THREE.Vector3()).y || 1);
    return this.scaleOf.get(b)!;
  }
  private add(b: THREE.Object3D, geo: THREE.BufferGeometry, color: number, parent?: THREE.Object3D) {
    const m = new THREE.Mesh(geo, flat(color));
    m.castShadow = true;
    m.frustumCulled = false;
    (parent ?? b).add(m);
    return m;
  }

  // Visage : cheveux, yeux, nez (les infectés ont les yeux pâles)
  private face(head: THREE.Object3D, axis: THREE.Vector3, k: number, pal: Palette) {
    const B = basis(head, axis), len = axis.length();
    const put = (geo: THREE.BufferGeometry, color: number) => { geo.applyMatrix4(B); return this.add(head, geo, color); };
    this.hair = put(block(len * 0.3, 0.215 / k, 0.24 / k, len * 0.75), pal.hair);
    for (const s of [-1, 1]) { const g = new THREE.BoxGeometry(0.035 / k, 0.025 / k, 0.01 / k); g.translate(s * 0.05 / k, len * 0.55, 0.118 / k); put(g, this.infected ? 0xd8d2b0 : 0x1c1c1c); }
    const n = new THREE.BoxGeometry(0.03 / k, 0.05 / k, 0.03 / k); n.translate(0, len * 0.42, 0.125 / k); put(n, pal.skin);
  }

  // Protections et sac, créés à la première demande puis montrés ou cachés
  // helmet, vest : identifiant de l'objet porté (ou null) ; le modèle est celui de l'icône (gear.ts)
  setGear(helmet: string | null, vest: string | null, pack: boolean | string | null) {
    if (this.sculpted) { this.setSculptedGear(helmet, vest, pack); return; }
    pack = !!pack;
    if (helmet && !this.helmets.has(helmet)) this.helmets.set(helmet, this.attachHelmet(helmet));
    if (vest && !this.vests.has(vest)) this.vests.set(vest, this.attachVest(vest));
    for (const [id, m] of this.helmets) if (m) m.visible = id === helmet;
    for (const [id, m] of this.vests) if (m) m.visible = id === vest;
    if (this.hair) this.hair.visible = !helmet; // les cheveux ne traversent pas le casque
    if (this.head && this.headCut) { this.head.visible = !helmet; this.headCut.visible = !!helmet; }
    if (pack && !this.pack) this.pack = this.buildPack();
    if (this.pack) this.pack.visible = pack;
  }

  // Silex : la pièce sculptée est posée sur l'os à l'endroit exact où elle était sur le modèle en pose en T,
  // puis suit l'os (tête pour le casque, haut du dos pour le gilet et le sac). Sans fichier : le modèle fait en code.
  private gear = new Map<string, THREE.Object3D | null>();
  private setSculptedGear(helmet: string | null, vest: string | null, pack: boolean | string | null) {
    const packId = typeof pack === 'string' ? pack : pack ? 'b_patrol' : null;
    const want = [helmet, vest, packId].filter((x): x is string => !!x);
    for (const id of want) if (!this.gear.has(id)) this.gear.set(id, this.mountSculpted(id));
    for (const [id, m] of this.gear) if (m) m.visible = want.includes(id);
  }
  private mountSculpted(id: string): THREE.Object3D | null {
    const src = GEAR_MODELS.get(id), b = bone(this.root, GEAR_BONE[id[0]!] ?? 'Spine2');
    if (!src || !b || !this.fit) {
      // Pas encore de modèle sculpté pour cet objet : celui fait en code
      if (id[0] === 'h') return this.attachHelmet(id);
      if (id[0] === 'v') return this.attachVest(id);
      return this.buildPack();
    }
    const m = src.clone();
    m.traverse(o => { if ((o as THREE.Mesh).isMesh) { o.castShadow = true; o.frustumCulled = false; } });
    // Pose en T au moment de la création (aucune animation encore appliquée) : repère de l'os → monde inversé
    this.root.updateMatrixWorld(true);
    m.matrixAutoUpdate = false;
    m.matrix.copy(new THREE.Matrix4().copy(b.matrixWorld).invert().multiply(this.root.matrixWorld).multiply(this.fit));
    b.add(m);
    return m;
  }

  private group(b: THREE.Object3D, axis: THREE.Vector3) {
    const g = new THREE.Group();
    g.matrixAutoUpdate = false;
    g.matrix.copy(basis(b, axis));
    b.add(g);
    return g;
  }
  // Modèle en mètres placé dans le repère de l'os (mis à l'échelle de l'os)
  private mount(b: THREE.Object3D, axis: THREE.Vector3, model: THREE.Object3D, alongY: number) {
    const g = this.group(b, axis), k = this.k(b);
    model.scale.setScalar(1 / k);
    model.position.set(0, alongY, 0);
    model.traverse(o => { o.frustumCulled = false; });
    g.add(model);
    return g;
  }
  private attachHelmet(id: string) {
    const head = bone(this.root, 'Head'), top = bone(this.root, 'HeadTop_End');
    if (!head || !top) return null;
    return this.mount(head, top.position.clone(), buildHelmet(id), top.position.length() * HELMET_AT);
  }
  private attachVest(id: string) {
    const s1 = bone(this.root, 'Spine1'), s2 = bone(this.root, 'Spine2'), neck = bone(this.root, 'Neck');
    if (!s1 || !s2 || !neck) return null;
    const len = s2.position.length() + neck.position.length() * 0.9;
    const g = this.mount(s1, s2.position.clone(), buildVest(id, len * 0.95 * this.k(s1)), -len * 0.05);
    return g;
  }
  private buildPack() {
    const s1 = bone(this.root, 'Spine1'), s2 = bone(this.root, 'Spine2');
    if (!s1 || !s2) return null;
    const k = this.k(s1), len = s2.position.length();
    const g = this.group(s1, s2.position.clone());
    const body = new THREE.BoxGeometry(0.32 / k, 0.44 / k, 0.18 / k); body.translate(0, len * 0.9, -0.24 / k); this.add(g, body, GEAR.pack, g);
    const flap = new THREE.BoxGeometry(0.33 / k, 0.12 / k, 0.19 / k); flap.translate(0, len * 0.9 + 0.17 / k, -0.24 / k); this.add(g, flap, GEAR.packDark, g);
    const pocket = new THREE.BoxGeometry(0.22 / k, 0.14 / k, 0.06 / k); pocket.translate(0, len * 0.9 - 0.1 / k, -0.35 / k); this.add(g, pocket, GEAR.packDark, g);
    return g;
  }
}
